import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreHoriz as MoreIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Template {
  id: string;
  name: string;
  body: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplateFormData {
  name: string;
  body: string;
  category: string;
}

const BODY_MAX_LENGTH = 1600;
const NAME_MAX_LENGTH = 100;

const Templates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({ name: '', body: '', category: '' });
  const [formErrors, setFormErrors] = useState<Partial<TemplateFormData>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<Template | null>(null);

  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => { filterTemplates(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, templates]);

  const filterTemplates = () => {
    if (!searchQuery.trim()) { setFilteredTemplates(templates); return; }
    const q = searchQuery.toLowerCase();
    setFilteredTemplates(templates.filter(t => t.name.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)));
  };

  const loadTemplates = async () => {
    try {
      setLoading(true); setError('');
      const response = await axios.get('/api/templates');
      setTemplates(response.data.templates || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load templates');
    } finally { setLoading(false); }
  };

  const handleOpenDialog = (template?: Template) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ name: template.name, body: template.body, category: template.category || '' });
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', body: '', category: '' });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setEditingTemplate(null); setFormData({ name: '', body: '', category: '' }); setFormErrors({}); };

  const validateForm = (): boolean => {
    const errors: Partial<TemplateFormData> = {};
    if (!formData.name.trim()) errors.name = 'Template name is required';
    else if (formData.name.trim().length > NAME_MAX_LENGTH) errors.name = `Max ${NAME_MAX_LENGTH} characters`;
    if (!formData.body.trim()) errors.body = 'Template body is required';
    else if (formData.body.trim().length > BODY_MAX_LENGTH) errors.body = `Max ${BODY_MAX_LENGTH} characters`;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true); setError('');
      const payload = { name: formData.name.trim(), body: formData.body.trim() };
      if (editingTemplate) {
        await axios.put(`/api/templates/${editingTemplate.id}`, payload);
        setSuccess('Template updated successfully');
      } else {
        await axios.post('/api/templates', payload);
        setSuccess('Template created successfully');
      }
      handleCloseDialog(); loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to save template';
      if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
        setFormErrors({ name: 'A template with this name already exists' });
      } else { setError(errorMsg); }
    } finally { setSaving(false); }
  };

  const handleDeleteClick = (template: Template) => { setTemplateToDelete(template); setDeleteDialogOpen(true); setMenuAnchor(null); };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    try {
      setError('');
      await axios.delete(`/api/templates/${templateToDelete.id}`);
      setSuccess('Template deleted successfully'); loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to delete template'); }
    finally { setDeleteDialogOpen(false); setTemplateToDelete(null); }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: Template) => {
    setMenuAnchor(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => { setMenuAnchor(null); setMenuTemplate(null); };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 5) return `${diffWeeks}w ago`;
    return `${diffMonths}mo ago`;
  };

  const getCategoryColor = (name: string) => {
    const categories: Record<string, string> = {
      'security': '#e74c3c', 'otp': '#e74c3c',
      'marketing': '#6c5ce7', 'promo': '#6c5ce7',
      'support': '#888', 'billing': '#555',
      'onboarding': '#00b894', 'welcome': '#00b894',
    };
    const lower = name.toLowerCase();
    for (const [key, color] of Object.entries(categories)) {
      if (lower.includes(key)) return color;
    }
    return '#6c5ce7';
  };

  const getCategoryLabel = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.includes('otp') || lower.includes('verif') || lower.includes('code') || lower.includes('security')) return 'OTP / SECURITY';
    if (lower.includes('market') || lower.includes('promo') || lower.includes('sale') || lower.includes('flash')) return 'MARKETING';
    if (lower.includes('support') || lower.includes('ticket') || lower.includes('confirm')) return 'SUPPORT';
    if (lower.includes('bill') || lower.includes('payment') || lower.includes('invoice') || lower.includes('remind')) return 'BILLING';
    if (lower.includes('welcome') || lower.includes('onboard')) return 'ONBOARDING';
    return 'GENERAL';
  };

  // Format creation date for display
  const formatCreatedDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3436', mb: 0.5, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>Message Templates</Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>Manage and organize your reusable SMS snippets.</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 200 }, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment> }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#6c5ce7', textTransform: 'none', fontWeight: 600, borderRadius: 2, px: { xs: 2, md: 3 }, fontSize: { xs: '0.8rem', md: '0.875rem' }, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#5a4bd1' } }}
          >
            Create Template
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Card Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' }, gap: 2.5 }}>
        {filteredTemplates.map((template) => {
          const categoryLabel = getCategoryLabel(template.name);
          const categoryColor = getCategoryColor(template.name);
          return (
            <Box
              key={template.id}
              sx={{
                border: '1px solid #e8e8ef',
                borderRadius: 3,
                p: 2.5,
                bgcolor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 200,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
              }}
            >
              {/* Top: Category + Menu */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: categoryColor, letterSpacing: 0.5, fontSize: 10, textTransform: 'uppercase' }}>
                    {categoryLabel}
                  </Typography>
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, template)} sx={{ color: '#ccc' }}>
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#2d3436', mb: 1.5, lineHeight: 1.3 }}>
                  {template.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5, fontSize: 13, borderLeft: '2px solid #e8e8ef', pl: 1.5 }}>
                  "{template.body.length > 120 ? template.body.substring(0, 120) + '...' : template.body}"
                </Typography>
              </Box>

              {/* Bottom: Stats */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1.5, borderTop: '1px solid #f5f5f5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BarChartIcon sx={{ fontSize: 14, color: '#999' }} />
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 500, fontSize: 11 }}>
                    Created {formatCreatedDate(template.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#bbb', fontSize: 11 }}>
                  {formatTimeAgo(template.updatedAt || template.createdAt)}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Create New Template Card */}
        <Box
          onClick={() => handleOpenDialog()}
          sx={{
            border: '2px dashed #e0e0e0',
            borderRadius: 3,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { borderColor: '#6c5ce7', bgcolor: '#faf9ff' },
          }}
        >
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#f0edff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <AddIcon sx={{ color: '#6c5ce7', fontSize: 24 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436', mb: 0.3 }}>Create New Template</Typography>
          <Typography variant="caption" sx={{ color: '#999' }}>Add a reusable snippet</Typography>
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { if (menuTemplate) handleOpenDialog(menuTemplate); handleMenuClose(); }}>Edit</MenuItem>
        <MenuItem onClick={() => { if (menuTemplate) handleDeleteClick(menuTemplate); }} sx={{ color: '#e74c3c' }}>Delete</MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name || `${formData.name.length}/${NAME_MAX_LENGTH}`}
              required fullWidth autoFocus
              inputProps={{ maxLength: NAME_MAX_LENGTH }}
            />
            <TextField
              label="Category (optional)"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              fullWidth
              placeholder="e.g. Marketing, Support, Billing"
            />
            <TextField
              label="Template Body"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              error={!!formErrors.body}
              helperText={formErrors.body || `${formData.body.length}/${BODY_MAX_LENGTH} characters (${Math.ceil(formData.body.length / 160) || 0} segments)`}
              required fullWidth multiline rows={6}
              inputProps={{ maxLength: BODY_MAX_LENGTH }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ bgcolor: '#6c5ce7', '&:hover': { bgcolor: '#5a4bd1' } }}>
            {saving ? <CircularProgress size={24} /> : (editingTemplate ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setTemplateToDelete(null); }}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete "{templateToDelete?.name}"? This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setTemplateToDelete(null); }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Templates;
